import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FileText, ShoppingCart, Receipt, CreditCard, Briefcase, CalendarCheck, Palette, MessageCircle, Mail, CalendarClock, ExternalLink, Loader2, History, StickyNote, Phone, Users, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { QuickActivityBar } from './QuickActivityBar';
import { groupByDate, timeLabel } from '../../../lib/dateBuckets';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPES = {
  note: { label: 'Notes', Icon: StickyNote, cls: 'bg-yellow-100 text-yellow-800' },
  call: { label: 'Calls', Icon: Phone, cls: 'bg-lime-100 text-lime-800' },
  meeting: { label: 'Meetings', Icon: Users, cls: 'bg-fuchsia-100 text-fuchsia-800' },
  quote: { label: 'Quotes', Icon: FileText, cls: 'bg-blue-100 text-blue-700' },
  order: { label: 'Orders', Icon: ShoppingCart, cls: 'bg-purple-100 text-purple-700' },
  invoice: { label: 'Invoices', Icon: Receipt, cls: 'bg-amber-100 text-amber-700' },
  payment: { label: 'Payments', Icon: CreditCard, cls: 'bg-emerald-100 text-emerald-700' },
  project: { label: 'Projects', Icon: Briefcase, cls: 'bg-indigo-100 text-indigo-700' },
  consultation: { label: 'Consultations', Icon: CalendarCheck, cls: 'bg-cyan-100 text-cyan-700' },
  fabric_request: { label: 'Fabric requests', Icon: Palette, cls: 'bg-rose-100 text-rose-700' },
  chat: { label: 'Chats', Icon: MessageCircle, cls: 'bg-teal-100 text-teal-700' },
  email: { label: 'Emails', Icon: Mail, cls: 'bg-stone-200 text-stone-700' },
  scheduled: { label: 'Scheduled', Icon: CalendarClock, cls: 'bg-orange-100 text-orange-700' },
};
const money = (n) => (n == null ? null : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export const CustomerTimeline = ({ customerId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API_URL}/admin/customers/${customerId}/timeline`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => alive && setData(r.data)).catch(() => alive && setData({ items: [], counts: {} })).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [customerId, token, reloadKey]);
  const removeActivity = async (i) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await axios.delete(`${API_URL}/admin/customers/${customerId}/activities/${i.id}`, { headers: { Authorization: `Bearer ${token}` } }); setReloadKey((k) => k + 1); } catch { toast.error('Could not delete'); }
  };

  const items = useMemo(() => (data?.items || []).filter((i) => filter === 'all' || i.type === filter), [data, filter]);
  const groups = useMemo(() => groupByDate(items, (i) => i.at), [items]);

  const counts = data?.counts || {};
  const total = (data?.items || []).length;
  return (
    <div className="p-6 space-y-4" data-testid="customer-timeline">
      <QuickActivityBar customerId={customerId} token={token} onAdded={() => setReloadKey((k) => k + 1)} />
      {loading && !data ? <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div> : (<>
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setFilter('all')} className={`h-6 px-2 rounded-full text-[11px] border ${filter === 'all' ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`} data-testid="timeline-filter-all">All <span className="opacity-60">{total}</span></button>
        {Object.entries(TYPES).filter(([k]) => counts[k]).map(([k, t]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`h-6 px-2 rounded-full text-[11px] border inline-flex items-center gap-1 ${filter === k ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`} data-testid={`timeline-filter-${k}`}>
            <t.Icon className="w-3 h-3" />{t.label} <span className="opacity-60">{counts[k]}</span>
          </button>
        ))}
      </div>
      {groups.length === 0 ? (
        <div className="py-14 text-center text-neutral-500 bg-white border border-dashed rounded-xl" data-testid="timeline-empty"><History className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No activity yet for this customer</p></div>
      ) : groups.map((g) => (
        <section key={g.key} data-testid={`timeline-group-${g.key}`}>
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">{g.label} <span className="font-normal">· {g.items.length}</span></h4>
          <ol className="relative border-l border-neutral-200 ml-3 space-y-2">
            {g.items.map((i, idx) => {
              const t = TYPES[i.type] || TYPES.email;
              return (
                <li key={`${i.type}-${i.id}-${idx}`} className="ml-5 relative" data-testid={`timeline-item-${i.type}`}>
                  <span className={`absolute -left-[31px] top-1.5 w-6 h-6 rounded-full flex items-center justify-center ${t.cls}`}><t.Icon className="w-3 h-3" /></span>
                  <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2 flex items-center gap-3 hover:border-amber-300">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-neutral-900 truncate">{i.title}</p>
                      <p className={`text-[11px] text-neutral-500 ${i.deletable || i.type === 'note' ? 'whitespace-pre-wrap' : 'truncate'}`}>{[t.label.replace(/s$/, ''), i.subtitle].filter(Boolean).join(' · ')}{i.duration_min ? ` · ${i.duration_min} min` : ''}{i.follow_up_at ? ` · follow up ${new Date(i.follow_up_at).toLocaleDateString()}` : ''}{i.author ? ` — ${i.author}` : ''}</p>
                    </div>
                    {i.amount != null && <span className="text-xs font-medium tabular-nums text-neutral-800">{money(i.amount)}</span>}
                    {i.status && <span className={`text-[10px] px-1.5 py-px rounded-full ${i.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-neutral-100 text-neutral-600'}`}>{String(i.status).replace(/_/g, ' ')}</span>}
                    <span className="text-[10px] text-neutral-400 shrink-0 w-14 text-right">{timeLabel(i.at)}</span>
                    {i.tab && <a href={`/admin?tab=${i.tab}`} className="p-1 rounded text-neutral-400 hover:text-amber-700 hover:bg-amber-50" title={`Open ${t.label}`} data-testid={`timeline-open-${i.type}`}><ExternalLink className="w-3.5 h-3.5" /></a>}
                    {i.deletable && <button type="button" onClick={() => removeActivity(i)} className="p-1 rounded text-neutral-300 hover:text-red-600 hover:bg-red-50" title="Delete entry" data-testid={`timeline-delete-${i.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
      </>)}
    </div>
  );
};

export default CustomerTimeline;
